// src/pages/auth/LoginPage.js
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  // Handle Google OAuth callback with access token
  const handleGoogleOAuthSuccess = useCallback(async (tokenResponse) => {
    setLoading(true);
    try {
      // tokenResponse contains access_token from Google OAuth
      const user = await login(tokenResponse.access_token);
      
      // Only allow admin users to login
      if (user.role === 'admin') {
        toast.success(`Welcome ${user.name}!`);
        navigate('/admin');
      } else {
        // Candidate login is disabled - log them out and show error
        await logout();
        toast.error('Access denied. Only administrators can access this system.');
      }
      
      // Comment out old candidate navigation logic
      // if (user.role === 'admin') {
      //   navigate('/admin');
      // } else {
      //   navigate('/candidate');
      // }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }, [login, logout, navigate]);

  // Initialize Google OAuth for access tokens
  React.useEffect(() => {
    const initializeGoogleAuth = () => {
      if (window.google?.accounts) {
        // Initialize Google OAuth 2.0 for access tokens
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
          callback: handleGoogleOAuthSuccess,
        });

        // Create custom Google sign-in button
        const googleButton = document.getElementById('google-signin-button');
        if (googleButton) {
          googleButton.innerHTML = `
            <button type="button" class="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          `;
          
          googleButton.querySelector('button').addEventListener('click', () => {
            client.requestAccessToken();
          });
        }
      }
    };

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleAuth;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleGoogleOAuthSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Interview System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Admin Access Only - Sign in to access your dashboard
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm -space-y-px">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div id="google-signin-button" className="w-full"></div>
            )}
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Use your administrator Google account to sign in
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;